<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import howMagneticStormWindowsAttachWindowFrame from './images/howMagneticStormWindowsAttachWindowFrame.jpg';
	import livingRoomWithLargeWindows from './images/livingRoomWithLargeWindows.jpg';
	import porchWindowsWithShutters from './images/porchWindowsWithShutters.jpg';
	import womanInstallingSoundproofInserts from './images/womanInstallingSoundproofInserts.jpg';
</script>

<Section>
	<p>
		Historic homes are both a privilege and a pleasure to own and inhabit.
		Homeowners take great pride in preserving these magnificent landmarks,
		caring for their structures and aesthetics in ways that respect the
		buildings’ storied histories.
	</p>
	<p>
		Historic homeowners have options when updating their building: restore to a
		specific era, rehabilitate and update as needed, or preserve as is. However,
		when maintaining an old home for habitability, homeowners often prefer to
		keep the home’s character in mind, including its original single-pane
		windows.
	</p>
	<p>
		Whether interior or exterior, <a
			href="/resources/storm-windows-guide/traditional#materials"
			>storm windows are popular additions</a
		> because they keep the home’s overall aesthetic and original windows intact while
		improving energy efficiency and blocking outside noise. Magnetic storm windows
		are among the several styles to explore when deciding what storm windows will
		work best for your home.
	</p>
	<div class="grid justify-items-center gap-2">
		<Image
			alt="living room with large wall of windows with interior magnetic storm windows"
			src={livingRoomWithLargeWindows}
		/>
		<a
			href="https://www.pexels.com/photo/apartment-architecture-chairs-clean-276551"
			target="_blank"
			rel="external">Source</a
		>
	</div>
</Section>

<Section>
	<h2>What Are Magnetic Storm Windows?</h2>
	<p>
		Magnetic storm windows are a type of storm window with a metal frame
		installed around the window stop, after which the magnetic storm window is
		placed on the metal frame and held there by a strong magnetic field,
		creating a heavy-duty seal. This specific type of storm window has its own
		set of positives and negatives to consider.
	</p>
	<h3>Pros of Magnetic Storm Windows</h3>
	<ul>
		<li>
			<strong>Affordable price</strong>. Magnetic storm windows are one of the
			least expensive options available compared to other types of storm windows
			or replacing windows entirely.
		</li>
		<li>
			<strong>Lightweight</strong>. It is easy to move and handle magnetic storm
			windows since they are durable but not heavy, making them accessible and
			manageable for anyone.
		</li>
		<li>
			<strong>Ease of placement and removal</strong>. Interior magnetic storm
			windows are easy to place and takedown without the need for tools, thanks
			to their magnetic seal design. Removable magnetic window insulation is an
			excellent choice if you still want to be able to open your windows
			occasionally in the winter.
		</li>
		<li>
			<strong>Removes condensation</strong>. Accumulating ice and condensation
			on windows is a common problem during the winter. The strong seal of
			magnetic storm windows all but eliminates this issue.
		</li>
		<li>
			<strong>Noise reduction</strong>. Magnetic acrylic soundproof storm
			windows can block out up to 70% of outside noise when applied to a
			single-pane original window and up to 50% with a double-pane window
			(compared to replacement windows, which only reduce outside noise by
			approximately 25% and cost more). They are an excellent option for homes
			in busy neighborhoods or near busy roads. They also reduce sound exiting
			the house, enhancing privacy.
		</li>
		<li>
			<strong>Energy-efficient</strong>. The strong seal of interior magnetic
			storm windows increases the original window’s performance, acting as a
			barrier to cold drafts and helping the home retain heat in the winter.
			Additionally, interior magnetic storm windows are often treated with an
			acrylic coating, which protects the home against ultra-violet ray damage.
		</li>
		<li>
			<strong>Cost Saving</strong>. In addition to being
			<a href="/resources/storm-windows-guide/replacement"
				>less expensive to purchase and install than replacement windows</a
			>, magnetic storm windows often quickly pay for themselves through lower
			energy bills.
		</li>
	</ul>
	<Image
		alt="front porch with large windows and an alternative to magnetic storm windows"
		src={porchWindowsWithShutters}
	/>
	<h3>Cons of Magnetic Storm Windows</h3>
	<ul>
		<li>
			<strong>Seal deterioration</strong>. Although it will take several years,
			the magnetic seal can weaken with time, reducing the window’s efficiency.
		</li>
		<li>
			<strong>Aesthetic issues</strong>. Even applied on the interior, magnetic
			storm windows affect the home’s aesthetic. The metal bracket that holds
			magnetic windows in place is permanently attached to the window frame,
			remaining visible even when the magnetic windows are not in use. This is
			an especially problematic issue for homeowners who want to maintain the
			historic nature of their home.
		</li>
		<li>
			<strong>Single-direction seal</strong>. Magnets attract in one direction;
			therefore, magnetic windows only protect against airflow one way rather
			than fully from every direction around the window frame.
		</li>
		<li>
			<strong>Changing temperatures can cause failure</strong>. Most magnetic
			window inserts use acrylic that expands and contracts based on the
			temperature, while the metal bracket frame does not. The magnetic seal can
			break, and the whole panel can pop off the bracket when the window insert
			and magnets expand or contract.
		</li>
		<li>
			<strong>Lengthy installation process and possible damage</strong>.
			Magnetic storm windows often require a cumbersome installation that
			includes permanent adhesive, drilling, and extra hardware that needs to be
			measured and assembled upon arrival, not ahead of time. It can take
			several hours and alter the quality of your home. Due to the lengthy and
			complicated installation process, it is also challenging to uninstall
			entirely and may leave permanent damage to your window frames.
		</li>
		<li>
			<strong>Permanently damages window frame</strong>. Magnetic storm windows
			require permanent attachment of metal tracking to your window frame. When
			the panel is removed, the tracking remains. Even when if you remove the
			tracking, the adhesive and holes drilled into your window frame are
			difficult to remove and repair. This is a huge issue for historic,
			old-growth wood window frames, which are irreplaceable.
		</li>
	</ul>
	<Image
		alt="diagram of how interior magnetic storm windows work"
		src={howMagneticStormWindowsAttachWindowFrame}
	/>
</Section>

<Section>
	<h2>An Alternative to Magnetic Storm Windows</h2>
	<p>
		Another option to consider when searching for the right storm windows for
		your home is <a href="/resources/blog/magnetic-seal-vs-compression-seal"
			>compression seal inserts</a
		>.
	</p>
	<Image
		alt="woman installing Indow window inserts vs interior magnetic storm windows"
		src={womanInstallingSoundproofInserts}
	/>
	<p>
		Indow uses its patented silicone compression tube to keep window inserts in
		place instead of hardware like tracking. This saves your window frames from
		damage, keeping them beautiful for years to come.
	</p>
	<p>
		Built from silicone tubing that provides a seal from all directions, Indow
		compression seal inserts are stronger than magnetic storm windows. The
		inserts are resistant to changing temperatures that can cause failure in
		track-based systems. They absorb the expansion and contraction that comes
		with temperature shifts and provide excellent resistance to unwanted drafts
		and noise.
	</p>
	<p>
		Every Indow compression seal insert is custom made to each window’s
		specifications. This makes them particularly <a
			href="/resources/blog/storm-window-alternative"
			>well suited for historic homes</a
		> where each window will have a slightly different size due to the homes settling
		over the years.
	</p>
	<p>
		Find out more about the <a
			href="/resources/blog/magnetic-seal-vs-compression-seal"
			>Indow compression system</a
		>, and then
		<a href="/how-to/get-indow-inserts"
			>get a free consultation and estimate here</a
		>.
	</p>
</Section>

<Reviews />
